import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { APP_HAS_CHANGES_TEXT } from 'constants/texts';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';

import { setAppHasChangesAction } from '../../store/unsavedChanges';

/**
 * IMPORTANT: WHEN WORKING WITH THIS HOOK
 *
 * In the line below (const doesAppHaveChanges = useSelector(appHasChangesSelector) && !forcedDisabled;),
 * the hook is disabled in dev mode (when the app is running on localhost). This is done to prevent
 * the navigation blocking logic from being executed in the development environment.
 *
 * If you need to test the navigation blocking functionality or unsaved changes behavior,
 * you should modify the `forcedDisabled` variable.
 *
 * Please PAY ATTENTION to this before working with this hook to avoid issues
 * when reproducing bugs or testing changes.
 */

const forcedDisabled = window.location.hostname === 'localhost';

const useUnsavedChangesHistoryBlocker = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const doesAppHaveChanges = useSelector(appHasChangesSelector) && !forcedDisabled;

  /**
   * Please make sure to read the comment at the top of the file before working with this useEffect
   */
  useEffect(() => {
    if (!doesAppHaveChanges) {
      return;
    }

    const listener = (event) => {
      // Cancel the event
      event.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
      // Chrome requires returnValue to be set
      event.returnValue = APP_HAS_CHANGES_TEXT;
    };

    window.addEventListener('beforeunload', listener);
    return () => {
      window.removeEventListener('beforeunload', listener);
    };
  }, [doesAppHaveChanges]);

  /**
   * Please make sure to read the comment at the top of the file before working with this useEffect
   */
  useEffect(() => {
    const unblockTabs = () => dispatch(setAppHasChangesAction(false));

    // Block navigation and register a callback that
    // fires when a navigation attempt is blocked.
    const unblock = history.block((state) => {
      if (!doesAppHaveChanges || state.pathname === history.location.pathname) {
        return true;
      }

      if (window.confirm(APP_HAS_CHANGES_TEXT)) {
        unblock();
        unblockTabs();
      } else {
        return false;
      }
    });

    return () => unblock();
  }, [dispatch, doesAppHaveChanges, history]);
};

export default useUnsavedChangesHistoryBlocker;
